/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Workspace management", description = "Hightouch provides a full suite of platform governance features to ensure security and compliance through access and change management.", lastUpdatedDate = new Date(1688688000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Workspace management",
        description: "Hightouch provides a full suite of platform governance features to ensure security and compliance through access and change management.",
        lastUpdatedDate: "Fri Jul 07 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Core concepts",
          "depth": 0,
          "id": "core-concepts"
        }, {
          "value": "Access management",
          "depth": 0,
          "id": "access-management"
        }, {
          "value": "Invite team members",
          "depth": 1,
          "id": "invite-team-members"
        }, {
          "value": "Accept an invitation",
          "depth": 1,
          "id": "accept-an-invitation"
        }, {
          "value": "Remove team members",
          "depth": 1,
          "id": "remove-team-members"
        }, {
          "value": "Default role settings",
          "depth": 1,
          "id": "default-role-settings"
        }, {
          "value": "Change user roles",
          "depth": 1,
          "id": "change-user-roles"
        }, {
          "value": "Workspace authorization",
          "depth": 1,
          "id": "workspace-authorization"
        }, {
          "value": "Change management",
          "depth": 0,
          "id": "change-management"
        }, {
          "value": "Use approval flows",
          "depth": 1,
          "id": "use-approval-flows"
        }, {
          "value": "Configure environments",
          "depth": 1,
          "id": "configure-environments"
        }, {
          "value": "View audit logs",
          "depth": 1,
          "id": "view-audit-logs"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      strong: "strong",
      h2: "h2",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["Since Hightouch manages the flow of your business-critical data, we provide a full suite of platform governance features to ensure security and compliance. These features fall into two categories: ", _jsx(_components.a, {
          href: "#access-management",
          children: _jsx(_components.strong, {
            children: "access management"
          })
        }), " and ", _jsx(_components.a, {
          href: "#change-management",
          children: _jsx(_components.strong, {
            children: "change management"
          })
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Before going in-depth on these features, it's helpful to understand the core concepts Hightouch uses to structure workspace management features."
      }), "\n", _jsx(_components.h2, {
        id: "core-concepts",
        children: "Core concepts"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Organization"
          }), ": a container for a set of workspaces, used for billing purposes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace"
          }), ": a Hightouch account representing a set of resources and users. All settings apply at the workspace level, except for ", _jsx(_components.a, {
            href: "/workspace-management/sso-legacy",
            children: "SSO"
          }), ", which applies at the organization level."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Environment"
          }), ": a Hightouch workspace you've designated for development, staging, or production purposes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User"
          }), ": an individual with access to a workspace. A user can be associated with one or more workspaces."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Role"
          }), ": a set of policies governing a user's access to resources. Users can only be assigned one role."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Policy"
          }), ": a set of actions users can take on resources given certain conditions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Resource"
          }), ": any of the building blocks of a workspace: for example, sources, destinations, models, syncs, and audiences."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Linked resources"
          }), ": Resources of the same type, for example, sources, that are linked across environments."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/workspace-management-concepts.png",
          alt: "Core concepts"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When you have many resources in the same workspace, it can be helpful to organize and navigate them using ", _jsx(_components.a, {
          href: "/workspace-management/folders-filters#folders",
          children: "folders"
        }), " and ", _jsx(_components.a, {
          href: "/workspace-management/folders-filters#filters",
          children: "filters"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "access-management",
        children: "Access management"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's access management features let you govern who can access your workspaces and what actions they can take."
      }), "\n", _jsx(_components.p, {
        children: "Access management features include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/sso-legacy",
            children: _jsx(_components.strong, {
              children: "SAML single sign-on (SSO)"
            })
          }), " to support organizational access and user creation"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/rbac",
            children: _jsx(_components.strong, {
              children: "Role-based access controls (RBAC)"
            })
          }), " to govern and customize what actions users can take"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/sso-legacy#access-management-with-sso-groups",
            children: _jsx(_components.strong, {
              children: "SSO groups"
            })
          }), " to support workspace and role assignment at scale"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/lbac",
            children: _jsx(_components.strong, {
              children: "Label-based access controls (LBAC)"
            })
          }), " to provide deeper access granularity"]
        }), "\n"]
      }), "\n", _jsx(BtOnly, {
        featureName: "SAML SSO, SSO groups, role-based controls, and label-based controls",
        plural: true
      }), "\n", _jsx(_components.h3, {
        id: "invite-team-members",
        children: "Invite team members"
      }), "\n", _jsxs(_components.p, {
        children: ["You can choose to invite team members to your workspace manually or enable access for your entire organization using SAML SSO. Before inviting team members, you may want to ", _jsx(_components.a, {
          href: "#default-role-settings",
          children: "change the default role"
        }), " for a workspace or ", _jsx(_components.a, {
          href: "/workspace-management/sso-legacy#access-management-with-sso-groups",
          children: "set up SSO groups"
        }), " to map groups in your identity provider to Hightouch roles."]
      }), "\n", _jsxs(_components.p, {
        children: ["To manually invite a team member, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/members",
          children: [_jsx(_components.strong, {
            children: "Members"
          }), " tab"]
        }), " on the ", _jsx(_components.strong, {
          children: "Settings"
        }), " page and click ", _jsx(_components.strong, {
          children: "Invite new people"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/invite-new-people.png",
          alt: "Members tab in Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In the modal that appears, enter your team member's ", _jsx(_components.strong, {
          children: "Email"
        }), ", select the appropriate ", _jsx(_components.strong, {
          children: "Role"
        }), ", and click ", _jsx(_components.strong, {
          children: "Send invitation"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/send-invite.png",
          alt: "Invitation modal"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/members",
          children: [_jsx(_components.strong, {
            children: "Members"
          }), " tab"]
        }), " displays pending invitations and team members. You can choose to resend or cancel any pending invitations. Outstanding invitations are active for 30 days."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/cancel-resend-invite.png",
          alt: "Pending invitation in members tab"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Check out the ", _jsx(_components.a, {
          href: "/workspace-management/sso-legacy",
          children: "SSO documentation"
        }), " to learn how to configure SAML SSO to provide access for your entire organization through an identity provider like Okta or Azure Active Directory."]
      }), "\n", _jsx(_components.h3, {
        id: "accept-an-invitation",
        children: "Accept an invitation"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you receive the invitation to join your team's Hightouch workspace, click the ", _jsx(_components.strong, {
          children: "Join workspace"
        }), " button in the email to access the ", _jsx(_components.strong, {
          children: "Log in to Hightouch"
        }), " page."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/login-hightouch.png",
          alt: "Log in to Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Select the correct login method based on the credentials you would like to use to access the workspace.\nA wrong selection can cause an ", _jsx(_components.strong, {
          children: "Invalid invitation"
        }), " error."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/invalid-invitation.png",
          alt: "Invalid invitation"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If you see this error even after selecting the correct login method, we recommend logging in using an ", _jsx(_components.a, {
            href: "https://support.google.com/chrome/answer/95464",
            children: "incognito browser tab"
          }), ".\nThis can help avoid issues with your browser cookies interrupting the correct login flow."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If your email address is associated with a Google Account, click ", _jsx(_components.strong, {
          children: "Log in with Google"
        }), ".\nEnter your Google Account credentials to proceed."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/login-google.png",
          alt: "Log in with Google"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If your email address is associated with a Microsoft Account, click ", _jsx(_components.strong, {
          children: "Log in with Microsoft"
        }), ". Enter your Microsoft Account credentials to proceed with logging in."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/login-microsoft.png",
          alt: "Log in with Microsoft"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If your organization set up ", _jsx(_components.a, {
          href: "/workspace-management/sso-legacy",
          children: _jsx(_components.strong, {
            children: "Single Sign-on"
          })
        }), ", you don't need to receive and accept an invitation to join your Hightouch workspace.\nAs described in the ", _jsxs(_components.a, {
          href: "/workspace-management/sso-legacy#ive-enabled-sso-in-my-workspacehow-do-i-invite-the-rest-of-my-team",
          children: [_jsx(_components.strong, {
            children: "SSO FAQ"
          }), " section"]
        }), ", you can join your workspace directly by accessing the ", _jsx(_components.strong, {
          children: "Login URL"
        }), " displayed on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/sso",
          children: [_jsx(_components.strong, {
            children: "Single sign-on"
          }), " tab"]
        }), " of your company's Hightouch workspace."]
      }), "\n", _jsxs(_components.p, {
        children: ["Otherwise, you can click ", _jsx(_components.strong, {
          children: "Log in with SSO"
        }), " and insert your company's ", _jsx(_components.strong, {
          children: "Organization identifier"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/sso-organization-identifier.png",
          alt: "SSO organization identifier"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After successfully logging in, you appear as a new member on your Hightouch workspace's ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/members",
          children: [_jsx(_components.strong, {
            children: "Members"
          }), " tab"]
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You only need to accept an invitation once.\nTo access your Hightouch workspace in the future, go to the generic ", _jsx(_components.a, {
          href: "https://app.hightouch.com",
          children: "workspace access page"
        }), ".\nIf your login credentials are cached in your browser, you will be redirected to your Hightouch workspace. Otherwise, you will be redirected to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/login",
          children: "login page"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "When logging in, make sure to always select the same login method that you chose when logging in for the first time.\nThis prevents creating duplicate users."
        })
      }), "\n", _jsx(_components.h3, {
        id: "remove-team-members",
        children: "Remove team members"
      }), "\n", _jsxs(_components.p, {
        children: ["To remove team members, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/members",
          children: [_jsx(_components.strong, {
            children: "Members"
          }), " tab"]
        }), " on the ", _jsx(_components.strong, {
          children: "Settings"
        }), " page and check the boxes next to the members or members you would like to remove.\nThen click ", _jsx(_components.strong, {
          children: "Delete selected members"
        }), " at the top of the page and confirm your choice in the popup window."]
      }), "\n", _jsx(_components.p, {
        children: "If you remove a workspace member, all resources they created, such as models and syncs, will continue to exist —\nYou don't need to migrate them to a different member."
      }), "\n", _jsx(_components.p, {
        children: "However, it's best to reauthorize any source or destination configurations that use a removed member's credentials.\nBy reauthorizing them with a current member's credentials, you can avoid a credential's expiration interrupting your syncs."
      }), "\n", _jsxs(_components.p, {
        children: ["If possible, use ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Service_account",
          children: "service accounts"
        }), " to authorize connections with resources rather than individuals' user accounts."]
      }), "\n", _jsx(_components.h3, {
        id: "default-role-settings",
        children: "Default role settings"
      }), "\n", _jsxs(_components.p, {
        children: ["Unless you've setup ", _jsx(_components.a, {
          href: "#access-management-with-sso-groups",
          children: "SSO group mappings"
        }), " to Hightouch roles, Hightouch assigns new members the ", _jsx(_components.strong, {
          children: "default workspace role"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can change the default role for a workspace from the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/settings",
          children: _jsx(_components.strong, {
            children: "Workspace tab"
          })
        }), " on the ", _jsx(_components.strong, {
          children: "Settings"
        }), " page. Select the appropriate role for users from the dropdown under the ", _jsx(_components.strong, {
          children: "Default role"
        }), " header."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/change-default-role.png",
          alt: "Selecting the default role for all users in a workspace"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Be careful when downgrading the default role, for example from ", _jsx(_components.strong, {
            children: "Admin"
          }), " to ", _jsx(_components.strong, {
            children: "Viewer"
          }), ". You may be unintentionally stripping users of permissions. Review ", _jsx(_components.a, {
            href: "rbac#default-roles",
            children: "role definitions"
          }), " before setting the default role to select the appropriate role for your workspace."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["From the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/settings",
          children: _jsx(_components.strong, {
            children: "Workspace tab"
          })
        }), ", you can also toggle off ", _jsx(_components.strong, {
          children: "using the default role for SSO users"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/disable-default-role.png",
          alt: "Toggling off default role for users in a workspace"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Your organization may want to do this to restrict unintended access to your workspace. By toggling this setting off, users won't have access to workspaces that have ", _jsx(_components.a, {
          href: "/workspace-management/sso-legacy#saml-single-sign-on",
          children: "enabled SAML SSO"
        }), " unless they belong to an ", _jsx(_components.a, {
          href: "#access-management-with-sso-groups",
          children: "identity provider group that has been mapped to a Hightouch role"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "change-user-roles",
        children: "Change user roles"
      }), "\n", _jsxs(_components.p, {
        children: ["Unless you've setup ", _jsx(_components.a, {
          href: "#access-management-with-sso-groups",
          children: "SSO group mappings"
        }), " to Hightouch roles, you can change an individual user's role from the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/members",
          children: [_jsx(_components.strong, {
            children: "Members"
          }), " tab"]
        }), " on the ", _jsx(_components.strong, {
          children: "Settings"
        }), " page. This page displays a table of all users in your workspace."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each user row has a ", _jsx(_components.strong, {
          children: "Role"
        }), " dropdown where you can select that user's role. You can create custom roles for assignment using ", _jsx(_components.a, {
          href: "/workspace-management/rbac",
          children: "role-based access controls"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/change-user-role.png",
          alt: "Changing an individual user's role"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Changing individual user roles isn't allowed for workspaces using ", _jsx(_components.a, {
            href: "/workspace-management/sso-legacy#access-management-with-sso-groups",
            children: "SSO"
          }), " since we don't want to enable users to circumvent permissions set in their organization's identity provider."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Check out the ", _jsx(_components.a, {
          href: "/workspace-management/sso-legacy#access-management-with-sso-groups",
          children: "SSO groups documentation"
        }), " to learn how to automatically assign roles to team members based on their group in your identity provider."]
      }), "\n", _jsx(_components.h3, {
        id: "workspace-authorization",
        children: "Workspace authorization"
      }), "\n", _jsxs(_components.p, {
        children: ["Workspace authorization settings determine the visibility and access to workspaces in your organization.\nYou can find ", _jsx(_components.strong, {
          children: "Workspace authorization"
        }), " settings in the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/workspace",
          children: [_jsx(_components.strong, {
            children: "Workspace"
          }), " settings tab"]
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/workspace-authorization.png",
          alt: "Workspace authorization"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The first setting, ", _jsx(_components.strong, {
          children: "Which domains can see this workspace?"
        }), ", determines the workspace's visibility.\nHightouch displays the workspace on your organization's ", _jsx(_components.a, {
          href: "https://app.hightouch.com/workspaces",
          children: "workspaces page"
        }), " for any domains you've configured."]
      }), "\n", _jsxs(_components.p, {
        children: ["The second setting, ", _jsx(_components.strong, {
          children: "Which domains can auto-join this workspace?"
        }), ", determines whether the workspace displays a ", _jsx(_components.strong, {
          children: "Join"
        }), " or ", _jsx(_components.strong, {
          children: "Request access"
        }), " button if a user with a particular domain isn't a member."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/select-a-workspace.png",
          alt: "Select a workspace"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If a user can join a workspace without approval, they can see a ", _jsx(_components.strong, {
          children: "Join"
        }), " button.\nAfter clicking it, they're automatically added as a new member to the workspace."]
      }), "\n", _jsxs(_components.p, {
        children: ["Otherwise, they must click the ", _jsx(_components.strong, {
          children: "Request access"
        }), " button.\nThis request appears in the ", _jsx(_components.strong, {
          children: "Awaiting approval"
        }), " section of the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/members",
          children: [_jsx(_components.strong, {
            children: "Members"
          }), " tab"]
        }), " and must be approved by a current member of the workspace by clicking ", _jsx(_components.strong, {
          children: "Approve access"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "change-management",
        children: "Change management"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's change management features let you enforce reviews of changes and allow you to view historical in-app changes."
      }), "\n", _jsx(_components.p, {
        children: "Change management features include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: _jsx(_components.strong, {
              children: "Approval flows"
            })
          }), " to stage changes before pushing them into production"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/environments",
            children: _jsx(_components.strong, {
              children: "Environments"
            })
          }), " to maintain separate workspaces and deploy changes to linked resources"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/audit-logs",
            children: _jsx(_components.strong, {
              children: "Audit logs"
            })
          }), " to provide records of what changes were made, by whom, and when"]
        }), "\n"]
      }), "\n", _jsx(BtOnly, {
        featureName: "Approval flows, environments, and audit logs",
        plural: true
      }), "\n", _jsx(_components.h3, {
        id: "use-approval-flows",
        children: "Use approval flows"
      }), "\n", _jsxs(_components.p, {
        children: ["Approval flows make it so that any changes made by users with the Workspace Draft Contributor role require approval from a privileged user. Check out the ", _jsx(_components.a, {
          href: "/workspace-management/approval-flows",
          children: "approval flows documentation"
        }), " to learn more."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-environments",
        children: "Configure environments"
      }), "\n", _jsxs(_components.p, {
        children: ["You can create environments in-app to organize and link resources between environments.\nYou can then deploy changes from one linked resource to another.\nCheck out the ", _jsx(_components.a, {
          href: "/workspace-management/environments",
          children: "environments documentation"
        }), " to learn more."]
      }), "\n", _jsx(_components.h3, {
        id: "view-audit-logs",
        children: "View audit logs"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch provides audit logs to track, trace, and search through historical actions taken in the Hightouch app. Check out the ", _jsx(_components.a, {
          href: "/workspace-management/audit-logs",
          children: "audit logs documentation"
        }), " to learn more."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
